import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';

export interface WidgetLink {
  id: string;
  user_id: string;
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
  created_at: string;
}

interface WidgetLinksState {
  links: WidgetLink[];
  loading: boolean;
  fetchLinks: () => Promise<void>;
  addLink: (
    sourceType: string,
    sourceId: string,
    targetType: string,
    targetId: string,
  ) => Promise<WidgetLink | null>;
  removeLink: (id: string) => Promise<boolean>;
  getLinksForSource: (sourceType: string, sourceId: string) => WidgetLink[];
  getLinksForTarget: (targetType: string, targetId: string) => WidgetLink[];
}

export const useWidgetLinksStore = create<WidgetLinksState>((set, get) => ({
  links: [],
  loading: false,

  fetchLinks: async () => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('widget_links')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (!error) set({ links: data ?? [] });
      });
    } catch (error) {
      handleException(error);
    } finally {
      set({ loading: false });
    }
  },

  addLink: async (sourceType, sourceId, targetType, targetId) => {
    try {
      const result = await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('widget_links')
          .insert({
            user_id: userId,
            source_type: sourceType,
            source_id: sourceId,
            target_type: targetType,
            target_id: targetId,
          })
          .select()
          .single();

        if (error) throw error;
        return data as WidgetLink;
      });

      if (result) {
        set((state) => ({ links: [result, ...state.links] }));
      }
      return result;
    } catch (error) {
      handleException(error);
      return null;
    }
  },

  removeLink: async (id: string) => {
    const deletedLink = get().links.find((l) => l.id === id);
    set((state) => ({ links: state.links.filter((l) => l.id !== id) }));

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('widget_links')
          .delete()
          .match({ id, user_id: userId });

        if (error) throw error;
      });
      return true;
    } catch (error) {
      handleException(error);
      if (deletedLink) {
        set((state) => ({ links: [deletedLink, ...state.links] }));
      }
      return false;
    }
  },

  getLinksForSource: (sourceType, sourceId) => {
    return get().links.filter(
      (l) => l.source_type === sourceType && l.source_id === sourceId,
    );
  },

  getLinksForTarget: (targetType, targetId) => {
    return get().links.filter(
      (l) => l.target_type === targetType && l.target_id === targetId,
    );
  },
}));
